import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useSession } from '../../lib/auth/session.js';
import { useRica, useRicaMutations, useRicaReviews, openRicaDocument, type Rica, type RicaKind } from '../../lib/api/hooks-rica.js';
import { Badge, CardHead, Empty, ErrorBanner, SectionHead, TableSkeleton } from '../../components/ui/Primitives.js';
import { Field } from '../../components/ui/Form.js';
import { FileField } from '../../components/ui/FileField.js';
import { RowAction } from '../../components/ui/RowAction.js';
import { Icon } from '../../components/ui/Icon.js';
import { confirmAction } from '../../components/ui/Confirm.js';
import { toastError } from '../../components/ui/Toast.js';
import { formatBytes, relativeTime } from '../../lib/format.js';

/**
 * RICA verification (owner, 25 October 2026; API modules/rica, 0170).
 *
 * The account's own people say what kind of customer they are, upload the documents that asks for,
 * and send them for review; the platform or the account's reseller approves them or sends them back
 * with a note. Until approved, numbers, airtime, automatic top-ups and outside calls wait -- said at
 * the top, plainly, since that is what makes the page matter. Staff see the same page on the account,
 * with the review underneath.
 */

const STATUS: Record<Rica['status'], { label: string; tone: 'ok' | 'warn' | 'danger' | 'muted' | 'info' }> = {
  not_started: { label: 'not started', tone: 'muted' },
  submitted: { label: 'with the reviewer', tone: 'info' },
  approved: { label: 'approved', tone: 'ok' },
  rejected: { label: 'sent back', tone: 'danger' },
};

export function RicaPage({ tenantId }: { tenantId: string }) {
  const can = useSession((s) => s.can);
  const q = useRica(tenantId);
  const m = useRicaMutations(tenantId);
  const r = q.data;
  const editable = can('rica:submit') && !!r && (r.status === 'not_started' || r.status === 'rejected');

  const [type, setType] = useState<'company' | 'individual'>('company');
  const [name, setName] = useState('');
  const [reg, setReg] = useState('');
  useEffect(() => {
    if (!r) return;
    setType(r.customer_type); setName(r.legal_name ?? ''); setReg(r.registration_no ?? '');
  }, [r?.customer_type, r?.legal_name, r?.registration_no]); // eslint-disable-line react-hooks/exhaustive-deps
  const detailsChanged = !!r && (type !== r.customer_type || name.trim() !== (r.legal_name ?? '') || (reg.trim() || null) !== (r.registration_no ?? null));

  if (q.error) return <><SectionHead title="RICA verification" /><ErrorBanner error={q.error} /></>;
  if (!r) return <><SectionHead title="RICA verification" /><div className="arc-card arc-card-body"><div className="arc-skeleton" style={{ height: 160 }} /></div></>;

  // What the chosen customer type asks for, before it is saved: the list follows the choice at once.
  const required = type === r.customer_type ? r.required : (type === 'company'
    ? [{ kind: 'cipc' as const, label: 'Company registration (CIPC)', hint: '' }, { kind: 'business_address' as const, label: 'Proof of business address', hint: '' }, { kind: 'id' as const, label: 'ID of the authorised person', hint: '' }]
    : [{ kind: 'id' as const, label: 'ID document', hint: '' }, { kind: 'address' as const, label: 'Proof of address', hint: '' }]);
  const missing = required.filter((x) => !r.documents.some((d) => d.kind === x.kind));
  const s = STATUS[r.status];

  return (
    <>
      <SectionHead title="RICA verification"
        sub="Who the account belongs to, as RICA requires before a phone service is used."
        action={<Badge tone={s.tone}><span className="arc-dot" aria-hidden="true" />{s.label}</Badge>} />

      {r.status !== 'approved' && (
        <div className="arc-banner arc-banner-warn" style={{ marginBottom: 14 }}>
          <Icon name="warning" size={14} />
          <span>
            {r.status === 'submitted'
              ? 'Your documents are with the reviewer. Until they are approved, new numbers, airtime and calls to outside numbers are paused. Internal and emergency calls work.'
              : 'Until RICA verification is approved, new numbers, airtime and calls to outside numbers are paused. Internal and emergency calls work.'}
          </span>
        </div>
      )}
      {r.status === 'rejected' && r.review_note && (
        <div className="arc-banner arc-banner-danger" style={{ marginBottom: 14 }}>
          <Icon name="warning" size={14} />
          <span><strong>Sent back{r.reviewer_name ? ` by ${r.reviewer_name}` : ''}:</strong> {r.review_note}</span>
        </div>
      )}
      {r.status === 'approved' && (
        <p className="arc-hint" style={{ margin: '0 0 14px' }}>
          {r.approved_how === 'existing_account'
            ? 'This account existed before RICA verification came in and was approved automatically.'
            : `Approved${r.reviewer_name ? ` by ${r.reviewer_name}` : ''}${r.reviewed_at ? ` ${relativeTime(r.reviewed_at)}` : ''}.`}
        </p>
      )}

      <div className="arc-card" style={{ marginBottom: 14 }}>
        <CardHead title="The customer" />
        <div className="arc-card-body arc-stack" style={{ gap: 12 }}>
          <div className="arc-kind-grid" role="radiogroup" aria-label="Customer type">
            {([['company', 'A company', 'Registered with CIPC: (Pty) Ltd, CC, NPC and the like.', 'accounts'],
              ['individual', 'A person or sole proprietor', 'Not registered as a company.', 'users']] as const).map(([v, label, hint, icon]) => (
              <button key={v} type="button" className="arc-kind" role="radio" aria-checked={type === v} data-on={type === v}
                disabled={!editable} onClick={() => setType(v)}>
                <span className="arc-kind-icon"><Icon name={icon} size={18} /></span>
                <span className="arc-kind-body"><span className="arc-kind-label">{label}</span><span className="arc-kind-hint">{hint}</span></span>
              </button>
            ))}
          </div>
          <div className="arc-grid arc-grid-2" style={{ gap: 12 }}>
            <Field label={type === 'company' ? 'Registered company name' : 'Full name'} htmlFor="rica-name">
              <input id="rica-name" className="arc-input" value={name} disabled={!editable} maxLength={200}
                onChange={(e) => setName(e.target.value)} placeholder={type === 'company' ? 'Meridian Logistics (Pty) Ltd' : 'As on the ID'} />
            </Field>
            {type === 'company' && (
              <Field label="Registration number" htmlFor="rica-reg" hint="As on the CIPC certificate, for example 2019/123456/07.">
                <input id="rica-reg" className="arc-input arc-mono" value={reg} disabled={!editable} maxLength={40}
                  onChange={(e) => setReg(e.target.value)} />
              </Field>
            )}
          </div>
          {editable && detailsChanged && (
            <div>
              <button type="button" className="arc-btn arc-btn-primary arc-btn-sm" disabled={m.details.isPending || name.trim().length < 2}
                onClick={() => m.details.mutate({ customer_type: type, legal_name: name.trim(), registration_no: type === 'company' ? reg.trim() || null : null })}>
                {m.details.isPending ? 'Saving…' : 'Save'}
              </button>
            </div>
          )}
          {m.details.error && <ErrorBanner error={m.details.error} />}
        </div>
      </div>

      <div className="arc-card" style={{ marginBottom: 14 }}>
        <CardHead title="Documents" sub="PDF, JPEG or PNG, up to 10 MB each. Kept in South Africa, encrypted, and shown only to the account and its reviewers." />
        <ul className="arc-rica-docs">
          {required.map((x) => {
            const docs = r.documents.filter((d) => d.kind === x.kind);
            return (
              <li key={x.kind}>
                <div className="arc-stack" style={{ gap: 2, minWidth: 0, flex: 1 }}>
                  <strong className="arc-row" style={{ gap: 6 }}>
                    {docs.length > 0 ? <Icon name="check" size={14} /> : <span className="arc-rica-dot" aria-hidden="true" />}
                    {x.label}
                  </strong>
                  {x.hint && <span className="arc-subtle" style={{ fontSize: 12 }}>{x.hint}</span>}
                  {docs.map((d) => (
                    <span key={d.id} className="arc-row" style={{ gap: 6, fontSize: 12.5 }}>
                      <button type="button" className="arc-link-btn arc-truncate" onClick={() => { void openRicaDocument(tenantId, d.id, d.file_name).catch(toastError); }}>
                        {d.file_name}
                      </button>
                      <span className="arc-subtle">{formatBytes(d.bytes)} · {relativeTime(d.uploaded_at)}</span>
                      {editable && (
                        <RowAction kind="delete" label={`Remove ${d.file_name}`} onClick={() => {
                          void confirmAction({ title: `Remove ${d.file_name}?`, action: 'Remove', danger: true })
                            .then((ok) => { if (ok) m.remove.mutate(d.id); });
                        }} />
                      )}
                    </span>
                  ))}
                </div>
                {editable && (
                  <UploadButton kind={x.kind} accept={r.accepted.join(',')} busy={m.upload.isPending && m.upload.variables?.kind === x.kind}
                    onFile={(file) => m.upload.mutate({ kind: x.kind, file })} again={docs.length > 0} />
                )}
              </li>
            );
          })}
        </ul>
        {(m.upload.error || m.remove.error) && <div className="arc-card-body"><ErrorBanner error={m.upload.error ?? m.remove.error} /></div>}
        {editable && (
          <div className="arc-card-body arc-row" style={{ gap: 10, flexWrap: 'wrap' }}>
            <button type="button" className="arc-btn arc-btn-primary"
              disabled={m.submit.isPending || missing.length > 0 || !r.legal_name || detailsChanged}
              onClick={() => m.submit.mutate()}>
              {m.submit.isPending ? 'Sending…' : 'Send for review'}
            </button>
            <span className="arc-subtle" style={{ fontSize: 12.5 }}>
              {detailsChanged ? 'Save the customer details first.'
                : !r.legal_name ? 'Add the name above first.'
                  : missing.length > 0 ? `Still needed: ${missing.map((x) => x.label).join(', ')}.`
                    : 'Everything is here. A reviewer usually answers within a working day.'}
            </span>
            {m.submit.error && <ErrorBanner error={m.submit.error} />}
          </div>
        )}
      </div>

      {can('rica:review') && r.status === 'submitted' && <ReviewCard tenantId={tenantId} />}
      {can('rica:review') && r.status === 'approved' && <WithdrawCard tenantId={tenantId} automatic={r.approved_how === 'existing_account'} />}
    </>
  );
}

function UploadButton({ kind, accept, busy, onFile, again }: {
  kind: RicaKind; accept: string; busy: boolean; onFile: (f: File) => void; again: boolean;
}) {
  return (
    <div style={{ minWidth: 180 }}>
      <FileField id={`rica-${kind}`} accept={accept} fileName={busy ? 'Uploading…' : null}
        ariaLabel={again ? 'Add another file' : 'Upload'} disabled={busy} onFile={onFile} />
    </div>
  );
}

/** The reviewer's decision: approve, or send back with what to fix. */
function ReviewCard({ tenantId }: { tenantId: string }) {
  const m = useRicaMutations(tenantId);
  const [note, setNote] = useState('');
  return (
    <div className="arc-card">
      <CardHead title="Review" sub="Check the documents match the name and number above, are legible and, for addresses, under 3 months old." />
      <div className="arc-card-body arc-stack" style={{ gap: 10 }}>
        <Field label="Note" htmlFor="rica-note" hint="Required when sending back: the account sees it and knows what to fix.">
          <textarea id="rica-note" className="arc-input" rows={2} maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
        <div className="arc-row" style={{ gap: 8 }}>
          <button type="button" className="arc-btn arc-btn-primary" disabled={m.review.isPending}
            onClick={() => {
              void confirmAction({
                title: 'Approve RICA verification?',
                body: 'Numbers can be assigned, airtime bought, and outside calls start working within a minute.',
                action: 'Approve',
              }).then((ok) => { if (ok) m.review.mutate({ decision: 'approve', ...(note.trim() ? { note: note.trim() } : {}) }); });
            }}>Approve</button>
          <button type="button" className="arc-btn" disabled={m.review.isPending || !note.trim()}
            onClick={() => m.review.mutate({ decision: 'reject', note: note.trim() })}>Send back</button>
        </div>
        {m.review.error && <ErrorBanner error={m.review.error} />}
      </div>
    </div>
  );
}

/**
 * Reviewers: withdraw an approval. For documents found to be wrong later, or to ask an account that
 * was approved automatically when RICA came in for its documents.
 */
function WithdrawCard({ tenantId, automatic }: { tenantId: string; automatic: boolean }) {
  const m = useRicaMutations(tenantId);
  const [note, setNote] = useState('');
  return (
    <div className="arc-card">
      <CardHead title="Withdraw approval"
        sub={automatic
          ? 'This account was approved automatically when RICA verification came in. Withdraw it to ask for its documents.'
          : 'For documents found to be wrong after they were approved.'} />
      <div className="arc-card-body arc-stack" style={{ gap: 10 }}>
        <Field label="Reason" htmlFor="rica-revoke-note" hint="Required. The account's admins are emailed it, with what to send.">
          <textarea id="rica-revoke-note" className="arc-input" rows={2} maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
        <div className="arc-row" style={{ gap: 8 }}>
          <button type="button" className="arc-btn arc-btn-danger" disabled={m.revoke.isPending || note.trim().length < 5}
            onClick={() => {
              void confirmAction({
                title: 'Withdraw RICA approval?',
                body: 'Within a minute the account can no longer call outside numbers, be given numbers, buy airtime or top up automatically, until new documents are approved. Internal and emergency calls keep working.',
                action: 'Withdraw approval',
                danger: true,
              }).then((ok) => { if (ok) m.revoke.mutate(note.trim(), { onSuccess: () => setNote('') }); });
            }}>Withdraw approval</button>
        </div>
        {m.revoke.error && <ErrorBanner error={m.revoke.error} />}
      </div>
    </div>
  );
}

/** Staff: the accounts waiting for review, the platform's all, a reseller's its own. */
export function RicaReviewsPage() {
  const q = useRicaReviews();
  const rows = q.data?.data ?? [];
  return (
    <>
      <SectionHead title="RICA reviews" sub="Accounts that have sent their documents and are waiting for a decision." />
      {q.error && <ErrorBanner error={q.error} />}
      <div className="arc-card">
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead><tr><th>Account</th><th>Customer</th><th>Documents</th><th>Sent</th><th /></tr></thead>
            {q.data === undefined ? <TableSkeleton query={q} cols={5} /> : (
              <tbody>
                {rows.map((r) => (
                  <tr key={r.tenant_id}>
                    <td><strong>{r.name}</strong>{r.legal_name && <div className="arc-subtle" style={{ fontSize: 12 }}>{r.legal_name}</div>}</td>
                    <td>{r.customer_type === 'company' ? 'Company' : 'Person'}</td>
                    <td className="arc-num">{r.documents}</td>
                    <td className="arc-subtle">{r.submitted_at ? relativeTime(r.submitted_at) : '—'}</td>
                    <td className="arc-td-right">
                      <Link className="arc-btn arc-btn-sm" to={`/manage/${r.slug}/rica` as string}>Review</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>
        {q.data && rows.length === 0 && <Empty icon="shield" title="Nothing waiting" hint="Accounts appear here when they send their RICA documents." />}
      </div>
    </>
  );
}
